import { useCallback, useContext, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEStaff, selectLoggedIn } from 'selectors/accountSelectors'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import LineBarsIcon from 'LuxKit/Icons/line/LineBarsIcon'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineCommentEditIcon from 'LuxKit/Icons/line/LineCommentEditIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Label from 'LuxKit/Label/Label'
import Group from 'components/utils/Group'
import ContactOptions from 'components/Common/ContactOptions'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type { TripPlannerChat } from 'tripPlanner/api/types'
import {
  TripPlannerStickyHeader,
  TripPlannerStyledLogo,
  LeftContent,
  RightContent,
} from 'tripPlanner/components/Header/Shared'
import { useDeleteTrip, useRecentTrips } from 'tripPlanner/hooks/api/trip'
import { getChatLastActivityTimestamp } from 'tripPlanner/utils/chatTimestamps'
import { useChatHistory } from './ChatTripPlannerPage/hooks/useChatHistory'
import { formatChatLabel } from './ChatTripPlannerPage/utils/format'
import { findClosestChat } from './ChatTripPlannerPage/utils/findClosestChat'
import ConfirmDeleteChatModal from 'tripPlanner/components/Chat/ConfirmDeleteChatModal'
import TripPlannerDrawer from './TripPlannerDrawer'
import PromptModal, { type PromptModalResult } from './PromptModal'
import { usePromptManagementContext } from './ChatTripPlannerPage/contexts/PromptManagementContext'
import Clickable from 'components/Common/Clickable'
import NotificationBanner from 'components/App/Header/HeaderBanners/NotificationBanner'

const TertiaryHeader = styled(Group)`
  background-color: ${props => props.theme.palette.neutral.default.seven};
  padding: ${rem(4)} ${rem(16)};
`

const LogoWrapper = styled.div`
  display: inline-flex;
  align-items: center;
`

const StyledStickyHeader = styled(TripPlannerStickyHeader)`
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.five};
`

interface Props {
  onStartNewChat: () => void
  onChatSelect: (chatId: number) => void
  onChatDeleted: (deletedChatId: number, fallbackChatId: number | undefined) => void
  isDevMode: boolean
  onDevModeToggle: () => void
  className?: string
}

function TripPlannerHeader({
  onStartNewChat,
  onChatSelect,
  onChatDeleted,
  isDevMode,
  onDevModeToggle,
  className,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  // ── Own data reads ──

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const isStaff = useAppSelector(isLEStaff)

  const {
    chatHistory, recentChats, refetchChatList, deleteChatMutation,
  } = useChatHistory({ isLoggedIn })

  const { data: previousTrips, refetch: refetchTrips } = useRecentTrips()
  const { mutateAsync: deleteTripMutation } = useDeleteTrip({
    onSuccess: () => {
      refetchTrips()
    },
    onError: (error) => {
      console.error('[TripChat] Failed to delete trip', error)
      showSnackbar(intl.formatMessage({
        id: 'FtZ79RamZe2rLll_1NZF',
        defaultMessage: 'Unable to delete trip. Please try again.',
        description: 'Error snackbar message when trip deletion fails in the trip planner AI chat',
      }), 'critical')
    },
  })

  const showTripPlansSection = (previousTrips?.length ?? 0) > 0

  // ── Prompt management (from context) ──

  const canManagePrompts = isStaff

  const {
    promptOptions: prompts,
    selectedPromptKey,
    handlePromptSelect: onPromptSelect,
    handlePromptAdd,
    loadPrompts,
  } = usePromptManagementContext()

  useEffect(() => {
    loadPrompts()
  }, [loadPrompts])

  const handlePromptAddWithModal = useCallback(async() => {
    const result = await showModal<PromptModalResult | undefined>(<PromptModal />)
    if (result) {
      await handlePromptAdd(result)
    }
  }, [showModal, handlePromptAdd])

  // ── Delete handlers ──

  const getChatDisplayName = useCallback((chat: TripPlannerChat | undefined) => {
    if (!chat) {
      return intl.formatMessage({
        id: '-LmD19gmaBgkOCUWrnKU',
        defaultMessage: 'this chat',
        description: 'Fallback display name for a trip planner chat when no chat is found',
      })
    }
    const trimmedName = chat.name?.trim()
    if (trimmedName) {
      return trimmedName
    }
    return formatChatLabel(getChatLastActivityTimestamp(chat), intl)
  }, [intl])

  const handleDeleteChat = useCallback(async(chatIdToDelete: number) => {
    const chatToDelete = chatHistory.find((chat) => chat.id === chatIdToDelete)
    const chatName = getChatDisplayName(chatToDelete)

    const confirmed = await showModal<boolean>(
      <ConfirmDeleteChatModal name={chatName} />,
    )

    if (!confirmed) {
      return
    }

    const fallbackChatId = findClosestChat(chatHistory, chatIdToDelete)

    try {
      await deleteChatMutation({ chatId: chatIdToDelete })
      await refetchChatList()
      onChatDeleted(chatIdToDelete, fallbackChatId)
    } catch(error) {
      console.error('[TripChat] Failed to delete chat', error)
      showSnackbar(intl.formatMessage({
        id: 'GjRGiJeZErGMMw8bQjrE',
        defaultMessage: 'Unable to delete chat. Please try again.',
        description: 'Error snackbar message when chat deletion fails in the trip planner AI chat',
      }), 'critical')
    }
  }, [chatHistory, showModal, deleteChatMutation, refetchChatList, getChatDisplayName, onChatDeleted, intl])

  const handleDeleteTrip = useCallback(async(tripIdToDelete: string) => {
    const tripToDelete = previousTrips?.find((trip) => trip.id === tripIdToDelete)
    const tripName = tripToDelete?.name ?? intl.formatMessage({
      id: 'qNXsaB7GsvlaNr5QLIZf',
      defaultMessage: 'Untitled Trip',
      description: 'Fallback name for a trip without a title in the delete confirmation modal',
    })

    const confirmed = await showModal<boolean>(
      <ConfirmDeleteChatModal name={tripName} type="trip" />,
    )

    if (!confirmed) {
      return
    }

    await deleteTripMutation(tripIdToDelete)
  }, [previousTrips, showModal, deleteTripMutation, intl])

  // ── Drawer ──

  const handleOpenDrawer = useCallback(() => {
    showModal(
      <TripPlannerDrawer
        onStartNewChat={onStartNewChat}
        recentChats={recentChats}
        recentTrips={previousTrips}
        onChatSelect={onChatSelect}
        onChatDelete={handleDeleteChat}
        onTripDelete={handleDeleteTrip}
        showTripPlansSection={showTripPlansSection}
        isStaff={isStaff}
        isDevMode={isDevMode}
        onDevModeToggle={onDevModeToggle}
        canManagePrompts={canManagePrompts}
        prompts={prompts}
        selectedPromptKey={selectedPromptKey}
        onPromptSelect={onPromptSelect}
        onPromptAdd={handlePromptAddWithModal}
      />,
    )
  }, [showModal, onStartNewChat, recentChats, previousTrips, onChatSelect, handleDeleteChat, handleDeleteTrip, showTripPlansSection, isStaff, isDevMode, onDevModeToggle, canManagePrompts, prompts, selectedPromptKey, onPromptSelect, handlePromptAddWithModal])

  const handleLogin = useCallback(() => {
    showModal(<AccountAccessModal />)
  }, [showModal])

  // ── i18n ──

  const feedbackBanner: App.NotificationBanner = {
    bannerText: intl.formatMessage({
      id: 'pQmVgEMN6r97qbLsUU8o',
      description: 'Feedback banner text informing users they have been selected to try AI Trip Planner',
      defaultMessage: 'Hi there! You\'ve been selected to try our new chat-based AI Trip Planner.',
    }),
    linkText: intl.formatMessage({
      id: 'M8BRqMk0oEdL7TSRoyxS',
      defaultMessage: 'Share your feedback',
      description: 'Link text in the feedback banner to open the TripPlanner feedback survey form',
    }),
    linkTo: 'https://usabi.li/do/gizugyzacokd/vj9hxb',
    openInNewTab: true,
    id: 'trip-planner-feedback-banner',
    slug: 'trip-planner-feedback-banner',
    show: true,
  }

  const loginButtonText = intl.formatMessage({
    id: 'ARcYTJ2nmHAv35KNSneu',
    defaultMessage: 'Log in to save your trip',
    description: 'Button text prompting unauthenticated users to log in to save their trip plan',
  })

  const backButtonText = intl.formatMessage({
    id: 'uvkQRhGsEtSZkBcb2NHS',
    defaultMessage: 'Back to Luxury Escapes',
    description: 'Navigation button text to return to the main Luxury Escapes site from TripPlanner',
  })

  const giveFeedbackText = intl.formatMessage({
    id: 'DwyX2RGwDgu5GbANxFYx',
    defaultMessage: 'Give feedback',
    description: 'Button text in the TripPlanner header to open the feedback survey for the feature',
  })

  const openMenuAriaLabel = intl.formatMessage({
    id: 'sIGePGauk8KDHGHEBhex',
    defaultMessage: 'Open menu',
    description: 'Accessible label for the hamburger menu button in the TripPlanner header bar',
  })

  const aiTripPlannerLabel = intl.formatMessage({
    id: 'EYQsWpC_SwW6ZS0Xb0Yz',
    defaultMessage: 'AI TripPlanner',
    description: 'Branding label for the AI TripPlanner feature in the header',
  })
  const betaLabel = intl.formatMessage({
    id: 'gO283xrN3xwQSsK25Jy9',
    defaultMessage: 'Beta',
    description: 'Beta badge label for AI TripPlanner in the header in the trip planner AI chat',
  })

  const rightContent = useMemo(() => {
    if (isLoggedIn) {
      return undefined
    }

    return (
      <TextButton
        kind="secondary"
        size="medium"
        onClick={handleLogin}
      >
        {loginButtonText}
      </TextButton>
    )
  }, [isLoggedIn, handleLogin, loginButtonText])

  return (
    <Group direction="vertical" className={className}>
      <TertiaryHeader
        direction="horizontal"
        horizontalAlign="space-between"
        verticalAlign="center"
        fullWidth
      >
        <TextButton
          kind="tertiary"
          size="small"
          startIcon={<LineArrowLeftIcon />}
          to="/"
        >
          {backButtonText}
        </TextButton>
        <Group direction="horizontal" gap={16} verticalAlign="center">
          <ContactOptions />
          <TextButton
            kind="tertiary"
            size="small"
            startIcon={<LineCommentEditIcon />}
            href="https://usabi.li/do/gizugyzacokd/vj9hxb"
            target="_blank"
            rel="noopener noreferrer"
          >
            {giveFeedbackText}
          </TextButton>
        </Group>
      </TertiaryHeader>
      <NotificationBanner overrideBanner={feedbackBanner} />
      <StyledStickyHeader>
        <LeftContent>
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <IconButton
              kind="tertiary"
              size="medium"
              onClick={handleOpenDrawer}
              aria-label={openMenuAriaLabel}
            >
              <LineBarsIcon />
            </IconButton>
            <Clickable to="/trip-planner/chat-trip-planner/new">
              <Group direction="horizontal" gap={8} verticalAlign="center">
                <LogoWrapper>
                  <TripPlannerStyledLogo />
                </LogoWrapper>
                <BodyText variant="large" weight="bold">{aiTripPlannerLabel}</BodyText>
                <Label kind="primary" variant="highlight">{betaLabel}</Label>
              </Group>
            </Clickable>
          </Group>
        </LeftContent>
        {rightContent && (
          <RightContent>
            {rightContent}
          </RightContent>
        )}
      </StyledStickyHeader>
    </Group>
  )
}

export default TripPlannerHeader
